import { extractLegalAreaNames } from '~/utils/pendo/legal-areas'

export interface PendoContentMetadata {
  type: 'law' | 'book' | 'academic_article' | 'template' | 'journal_article'
  title: MaybeRefOrGetter<string>
  id: MaybeRefOrGetter<string>
  legalAreas?: MaybeRefOrGetter<string[] | null | undefined>
}

export function usePendoContentTracking(metadata: PendoContentMetadata) {
  if (!import.meta.client) {
    return
  }

  const { pendoEnabled } = useRuntimeConfig().public
  if (!isPendoEnabled(pendoEnabled)) {
    return
  }

  const { loggedIn } = useUserSession()
  const { track } = usePendo()
  const { analytics } = useCookieConsent()

  const contentId = computed(() => toValue(metadata.id))
  const contentTitle = computed(() => toValue(metadata.title))
  const contentLegalAreas = computed(() => toValue(metadata.legalAreas))

  let lastTrackedId: string | null = null
  let inFlightId: string | null = null

  watch([contentId, contentTitle, contentLegalAreas, loggedIn, analytics], async ([id, title, legalAreas, isLoggedIn, isAnalyticsGranted]) => {
    if (!isLoggedIn || !isAnalyticsGranted || !id || !title || id === lastTrackedId || id === inFlightId) {
      return
    }

    // Prevent firing until legal areas have been loaded. `undefined` indicates no legal areas, while `null` indicates not loaded yet.
    if (legalAreas === null) {
      return
    }

    inFlightId = id

    const delivered = await track('ContentViewed', {
      title,
      contentId: id,
      type: metadata.type,
      legalAreas: extractLegalAreaNames(legalAreas ?? []).join(', '),
    })

    if (inFlightId === id) {
      inFlightId = null
    }

    if (delivered) {
      lastTrackedId = id
    }
  }, { immediate: true })
}
